import classNames from 'classnames';
import * as color from 'color';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import InfoBox from 'react-google-maps/lib/components/addons/InfoBox';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import { Tooltip } from '@mui/material';

import { ACL, BATTERY_ISSUE_PING_MSGS, getIconTabByName, UserCan } from '../../../../shared/constants';
import { IconTypes, iFullStoreState } from '../../../../shared/interfaces';
import { localStorage } from "../../../../shared/storage";
import { Actions } from '../../../../stores/reducers/gmap-reducers';
import { removeSelectedPoint, setSelectedPoint } from '../../../../stores/reducers/selectedPoint/AC';
import { FaVector } from '../../../elements/fa';
import { IInfoMarkerProps, IInfoMarkerPropsFromState } from './types';

import { getShowIconOnMap } from "../../../../shared/db/people-db";
import { getViewBoxByPath } from '../../../../utils';
import styles from './index.module.scss';

const mapStateToProps = (state: iFullStoreState, o: IInfoMarkerProps) => ({
  deviceDetails: state.devicesData.devicesDetails.get(o.deviceId),
  deviceLastPing: state.devicesData.devicesLastPing.get(o.deviceId),
  bounds: state.gmap.bounds,
});

export const loadIcon = async (
  url: string,
  iconColor: string = '#000'
): Promise<string> => {
  const response = await fetch(url);
  const content = await response.text();

  const isSvg = content.includes('svg');

  if (isSvg) {
    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(content, 'image/svg+xml');

    svgDocument
      .getElementsByTagName('svg')
      .item(0)
      ?.setAttribute('fill', iconColor.toUpperCase());

    const svgString = new XMLSerializer().serializeToString(svgDocument);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });

    return URL.createObjectURL(blob);
  } else {
    return url;
  }
};

const DeviceMarker = (props) => {
  const prevPropsRef = useRef<IInfoMarkerPropsFromState>();
  const { deviceDetails, deviceLastPing, dispatch, bounds } = props;
  const [markerIcon, setMarkerIcon] = useState<any>(null as any);
  const pingMsg = deviceLastPing?.msg ?? 'N/A';
  const hasSpeed = Boolean(deviceLastPing?.speed);
  const uid = (localStorage.get('login-init-user')).uid;
  const deviceHasImage = props.deviceDetails && props.deviceDetails.icon && props.deviceDetails.icon?.type === 2 && props.deviceDetails.icon?.url.includes('https://firebasestorage');
  const [hoverTimeoutId, setHoverTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const recenterToDevice = () => {
    const { dispatch, deviceId, doPan = false } = props;

    if (doPan) {
      dispatch(Actions.RECENTER_MAP_TO_DEVICE(deviceId, true));
    }
  };

  const updateMarker = async () => {
    if (!props.deviceDetails) return;

    const { size } = props;
    const { icon, color } = props.deviceDetails;
    const flag = (await getShowIconOnMap(uid)) || false;

    let iconType = icon?.type;
    let url: string;

    if (!iconType) {
      if ('fa' in icon) {
        iconType = IconTypes.FaIcon;
      } else if ('svgIconName' in icon) {
        iconType = IconTypes.SvgIcon;
      } else {
        iconType = IconTypes.UserSvgIcon;
      }
    }

    const shouldShowIcon = flag && iconType === IconTypes.UserSvgIcon;

    iconType = shouldShowIcon ? IconTypes.SvgIcon : iconType;

    switch (iconType) {
      case IconTypes.FaIcon:
        url = FaVector(icon.fa);
        break;
      case IconTypes.SvgIcon:
        const iconTab = (flag && deviceHasImage) ? getIconTabByName('truck') : getIconTabByName(icon.svgIconName);
        url = (flag && deviceHasImage) ? require(`../../../../assets/svg/map-marker.svg`) : require(`../../../../assets/svg/${iconTab}/${icon.svgIconName}.svg`);
        break;
      case IconTypes.UserSvgIcon:
        url = icon?.url;
        break;
    }

    if (iconType === IconTypes.SvgIcon) {
      setMarkerIcon(null);
      url = await loadIcon(url, color);
    }

    const fixedSize = (flag && deviceHasImage) ? 18 : 13;

    let marker;

    if (iconType === IconTypes.FaIcon) {
      marker = {
        path: url,
        type: iconType,
        anchor: new google.maps.Point(100, 500),
        scale: 0.06,
        fillColor: color || '#000',
        fillOpacity: 0.95,
        strokeWeight: 0,
      };
    } else if (iconType === IconTypes.UserSvgIcon) {
      marker = {
        url: url,
        type: iconType,
        size: new google.maps.Size(fixedSize * 3, fixedSize * 3),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(fixedSize, fixedSize * 3),
        scaledSize: new google.maps.Size(fixedSize * 3, fixedSize * 3),
        strokeWeight: 0,
      };
    } else {
      marker = {
        url: url,
        type: iconType,
        size: new google.maps.Size(fixedSize * 4, fixedSize * 4),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(fixedSize, fixedSize * 2.5),
        scaledSize: new google.maps.Size(fixedSize * 4, fixedSize * 4),
        strokeWeight: 0,
      };
    }

    setMarkerIcon(marker);
  };

  useLayoutEffect(() => {
    updateMarker();
  }, []);


  useEffect(() => {
    recenterToDevice();
  }, []);

  useEffect(() => {
    const prevProps = prevPropsRef.current;

    if (prevProps) {
      const prevLastLocation = prevProps.deviceLastPing?.coordinates.location;
      const lastLocation = props.deviceLastPing?.coordinates.location;

      if (!prevLastLocation && !!lastLocation) {
        recenterToDevice();
      }

      if (prevProps.deviceDetails?.icon !== props.deviceDetails?.icon) {
        updateMarker();
      }
    }

    prevPropsRef.current = props;
  }, [props]);

  const userCanDo = useSelector<iFullStoreState, Array<string>>((s) => s.auth.user?.acl?.can);
  const showMarkerNameAbove = ACL.check(UserCan.SHOW_MARKER_NAME_ABOVE, userCanDo, true);
  const roundedMapMarkerImage = ACL.check(UserCan.ROUNDED_MAP_MARKER_IMAGE, userCanDo, true);

  const labelStyle = useMemo((): React.CSSProperties => {
    if (!deviceDetails) return {};

    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      textAlign: 'center',
      width: 'auto',
      whiteSpace: 'nowrap',
      transform: 'translate(-50%, -50%)',
      left: '50%',
      top: 'calc(50% - 5px)',
      pointerEvents: 'none',
    };

    if (showMarkerNameAbove) {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: '#fff',
        fontFamily: 'Oswald, sans-serif',
        fontWeight: 700,
        textShadow: '0 0 3px #000, 0 0 6px #fff, 0 0 9px #000',
        fontSize: '10px',
        letterSpacing: '1px',
      };
    } else {
      return {
        ...baseStyle,
        backgroundColor: deviceDetails.color,
        color: color(deviceDetails.color).light() ? '#000' : '#fff',
        border: `1px solid ${color(deviceDetails.color).darken(0.5)}`,
        padding: '2px 6px',
        borderRadius: '3px',
      };
    }
  }, [showMarkerNameAbove, deviceDetails]);

  // calculate viewBox for path
  const viewBox = useMemo(() => {
    if (!(markerIcon && markerIcon.path)) return;

    return getViewBoxByPath(markerIcon.path);
  }, [markerIcon && markerIcon.path]);

  if (!deviceDetails) return null;

   if (deviceDetails?.isMarkerHidden === true || deviceDetails?.isArchived === true) return null;

  const {
    device: deviceId,
    tripId,
    pointId,
    coordinates: { location },
  } = deviceLastPing || { device: props.deviceId, tripId: null, pointId: null, coordinates: { location: null } };

  if (!location) return null;

  const renderDevice =
   props.skipBoundsCheck || (
    location.lat > bounds.latSouth &&
    location.lng > bounds.lngWest &&
    location.lat < bounds.latNorth &&
    location.lng < bounds.lngEast);

  if (!renderDevice) return null;

  const pingMsgCheck = [
    'Ignition on',
    'Acceleration Threshold',
    'Acceleration threshold',
    'Deceleration Threshold',
    'Deceleration threshold',
    'Idle Threshold',
    'Idle threshold',
    'Ignition On Report',
    'Ignition On Interval Report',
    'Direction Change',
    'Virtual Ignition On Interval Report',
    'Virtual Ignition On Report',
    'Move Start',
    'Move Stop',
    'Trip Start',
  ].includes(pingMsg);


  // HOVER LOGIC. DO NOT REMOVE IT!
  //
  // const removeSelectedPointOnTimeout = (id: string) => {
  //   const selectedPoint = store.getState().selectedPoint;
  //
  //   if (selectedPoint && selectedPoint.pointId === id && !selectedPoint.isClicked) {
  //     clearTimeout(hoverTimeoutId)
  //     setHoverTimeoutId(null);
  //     dispatch(removeSelectedPoint());
  //   }
  // }

  const onMarkerClick = () => {
    if (!deviceLastPing || !location) return;
    dispatch(removeSelectedPoint());
    dispatch(setSelectedPoint(deviceId, tripId, pointId, location, true));
  };

  // HOVER LOGIC. DO NOT REMOVE IT!
  //
  // const onHoverIn = () => {
  //   const selectedPoint = store.getState().selectedPoint;
  //
  //   if (!(selectedPoint && selectedPoint.isClicked === true)) {
  //     dispatch(removeSelectedPoint());
  //     dispatch(setSelectedPoint(deviceId, tripId, pointId, location, false));
  //
  //     if (hoverTimeoutId) {
  //       clearTimeout(hoverTimeoutId)
  //       setHoverTimeoutId(null);
  //     }
  //   }
  // }
  //
  // const onHoverOut = () => {
  //   const selectedPoint = store.getState().selectedPoint;
  //
  //   const timeoutId = setTimeout(() => {
  //     removeSelectedPointOnTimeout(selectedPoint.pointId);
  //   }, 5000);
  //
  //   setHoverTimeoutId(timeoutId);
  // }

  const displayAsSvgPath = markerIcon && !!markerIcon.path;

  const getIndicatorDot = () => {
    if (!deviceLastPing) {
      return (
        <Tooltip title="Not Tracked" arrow placement="top">
          <div className={`${styles.indicatorDot}`} style={{ backgroundColor: 'orange' }} />
        </Tooltip>
      );
    }

    if (deviceLastPing?.time && moment().diff(moment(deviceLastPing.time), 'weeks') >= 3) {
      return (
        <Tooltip title="Inactive 3 wks+" arrow placement="top">
          <div className={`${styles.indicatorDot} ${styles.grey}`} />
        </Tooltip>
      );
    }

    if (pingMsg === 'Heartbeat') {
      return (
        <Tooltip title="Heartbeat" arrow placement="top">
          <div className={`${styles.indicatorDot} ${styles.pink}`} />
        </Tooltip>
      );
    }

    const ignitionOffMsgs = [
      'Ignition off',
      'Ignition Off Report',
      'Ignition off Report',
      'Ignition Off Interval Report',
      'Virtual Ignition Off Report',
      'Trip Stop'
    ];

    if (ignitionOffMsgs.includes(pingMsg)) {
      return (
        <Tooltip title="Ignition OFF" arrow placement="top">
          <div className={`${styles.indicatorDot} ${styles.red}`} />
        </Tooltip>
      );
    }

    if (pingMsgCheck && !hasSpeed) {
      return (
        <Tooltip title="Idle" arrow placement="top">
          <div className={`${styles.indicatorDot} ${styles.yellow}`} />
        </Tooltip>
      );
    }

    if (pingMsgCheck && hasSpeed) {
      return (
        <Tooltip title="Ignition ON" arrow placement="top">
          <div className={`${styles.indicatorDot} ${styles.green}`} />
        </Tooltip>
      );
    }

    if (BATTERY_ISSUE_PING_MSGS.includes(pingMsg as typeof BATTERY_ISSUE_PING_MSGS[number])) {
      return (
        <Tooltip title="Battery Issue" arrow placement="top">
          <div className={`${styles.indicatorDot} ${styles.batteryIssue}`} />
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <>
      {markerIcon && (
        <InfoBox
          key={deviceLastPing?.pointId || deviceLastPing?.time}
          options={{
            disableAutoPan: true,
            zIndex: 4,
            boxStyle: {
              zIndex: 4,
              width: 'auto',
              overflow: 'visible',
              padding: '0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: '.9',
            },
            alignBottom: false,
            closeBoxURL: '',
            pixelOffset: new google.maps.Size(0, 0),
            enableEventPropagation: true,
          }}
          position={new google.maps.LatLng(location.lat, location.lng)}
        >
          <button
            type="button"
            className={styles.markerInfoContainer}
            onClick={onMarkerClick}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {displayAsSvgPath ? (
              <svg width={50} height={50} viewBox={viewBox} style={{ position: 'relative' }}>
                <path d={markerIcon.path} fill={markerIcon.fillColor} />
              </svg>
            ) : (
              <img
                src={markerIcon.url}
                alt={deviceDetails.name}
                width={50}
                height={50}
                className={styles.markerImage}
                style={{ borderRadius: roundedMapMarkerImage ? 6 : 0, position: 'relative' }}
              />
            )}

            <div
              className={classNames(styles.messageBlock, {
                [styles.onTheMarker]: showMarkerNameAbove,
              })}
              style={labelStyle}
            >
              <span style={{ height: '13px' }}>{deviceDetails.name}</span>
              <div className={styles.indicatorWrapper}>{getIndicatorDot()}</div>
            </div>
          </button>
        </InfoBox>
      )}
    </>
  )
}

export default compose(connect(mapStateToProps), React.memo)(DeviceMarker);
